import { motion } from "framer-motion";
import { Calendar, User, ArrowUpRight } from "lucide-react";
import Seo from "@/components/common/Seo";
import SectionHeading from "@/components/common/SectionHeading";
import GlowOrbs from "@/components/common/GlowOrbs";
import { BLOG_POSTS } from "@/lib/data";

export default function Blog() {
  const [first, ...rest] = BLOG_POSTS;
  return (
    <div data-testid="page-blog">
      <Seo title="Insights & Blog" description="Playbooks, benchmarks and hot takes on OPT, CPT, H-1B and US tech careers from SKVY Harmony's senior recruiters." path="/blog" />

      <section className="relative pt-16 pb-8 overflow-hidden">
        <GlowOrbs />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
          <SectionHeading
            eyebrow="Insights"
            title="Field notes from the frontline of US tech hiring."
            sub="Playbooks, benchmarks and hot takes from our senior US recruiters and FAANG-hired mentors."
          />
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-10">
          <motion.a
            href="#"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="group grid lg:grid-cols-[1.1fr_1fr] gap-8 rounded-3xl overflow-hidden border border-white/5 bg-[#0d1428] hover:border-[#10B981]/30 transition-colors"
            data-testid="blog-featured"
          >
            <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
              <img src={first.img} alt={first.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
              <span className="absolute top-4 left-4 font-inter text-[10px] uppercase tracking-[0.18em] px-3 py-1 rounded-full bg-black/60 border border-white/10 text-white">Featured</span>
            </div>
            <div className="p-8 lg:p-12 flex flex-col justify-center">
              <span className="font-inter text-[11px] uppercase tracking-[0.18em] text-[#10B981] font-semibold">{first.tag}</span>
              <h3 className="font-poppins text-white text-2xl sm:text-3xl font-semibold mt-3 leading-tight">{first.title}</h3>
              <p className="font-inter text-white/60 mt-4">{first.excerpt}</p>
              <div className="mt-6 flex items-center gap-4 font-inter text-xs text-white/50">
                <span className="flex items-center gap-1.5"><User className="w-3.5 h-3.5" /> {first.author}</span>
                <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> {first.date}</span>
                <span>• {first.readTime}</span>
              </div>
              <div className="mt-6 inline-flex items-center gap-1.5 text-[#10B981] font-inter text-sm">
                Read article <ArrowUpRight className="w-4 h-4" />
              </div>
            </div>
          </motion.a>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((p, i) => (
              <motion.a
                key={p.slug}
                href="#"
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: i * 0.04 }}
                className="group rounded-2xl overflow-hidden border border-white/5 bg-[#0d1428] hover:border-[#10B981]/30 transition-colors"
                data-testid={`blog-card-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" />
                </div>
                <div className="p-6">
                  <span className="font-inter text-[10px] uppercase tracking-[0.18em] text-[#10B981] font-semibold">{p.tag}</span>
                  <p className="font-poppins text-white text-lg font-semibold mt-2 leading-snug group-hover:text-[#10B981] transition-colors">{p.title}</p>
                  <p className="font-inter text-sm text-white/55 mt-2 line-clamp-2">{p.excerpt}</p>
                  <div className="mt-4 font-inter text-xs text-white/50 flex items-center gap-3">
                    <span>{p.date}</span>
                    <span>•</span>
                    <span>{p.readTime}</span>
                  </div>
                </div>
              </motion.a>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
